import React from 'react';

/**
 * Minimal markdown renderer for app/post descriptions. Supports the
 * subset we actually use in the catalogue:
 *
 *   ## Heading        → h3
 *   ### Subheading    → h4
 *   **bold**          → <strong>
 *   - item            → bullet list
 *   1. item           → numbered list
 *   blank line        → block break (paragraph)
 *   plain text        → paragraph
 *
 * No HTML escape concerns · React renders strings as text by default
 * and we never inject markup ourselves, so the whole thing is XSS-safe.
 */

const BOLD_RE = /\*\*(.+?)\*\*/g;

function renderInline(text, keyBase) {
  // Split on **bold** spans; even indices are plain text, odd indices
  // are the captured bold content.
  const parts = text.split(BOLD_RE);
  return parts.map((piece, i) =>
    i % 2 === 1
      ? <strong key={`${keyBase}-b${i}`}>{piece}</strong>
      : <React.Fragment key={`${keyBase}-t${i}`}>{piece}</React.Fragment>
  );
}

/* Group consecutive lines into blocks · paragraph, heading, bullet
 * list, numbered list. Blank lines separate blocks. */
function toBlocks(src) {
  const lines = (src || '').replace(/\r\n/g, '\n').split('\n');
  const blocks = [];
  let buf = null;  // current open block { type, items: [...] }

  const flush = () => {
    if (buf) blocks.push(buf);
    buf = null;
  };

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '');
    if (line.trim() === '') { flush(); continue; }

    const h3 = /^###\s+(.+)$/.exec(line);
    if (h3) { flush(); blocks.push({ type: 'h4', text: h3[1] }); continue; }

    const h2 = /^##\s+(.+)$/.exec(line);
    if (h2) { flush(); blocks.push({ type: 'h3', text: h2[1] }); continue; }

    const ul = /^[-*]\s+(.+)$/.exec(line);
    if (ul) {
      if (buf?.type !== 'ul') { flush(); buf = { type: 'ul', items: [] }; }
      buf.items.push(ul[1]);
      continue;
    }

    const ol = /^\d+\.\s+(.+)$/.exec(line);
    if (ol) {
      if (buf?.type !== 'ol') { flush(); buf = { type: 'ol', items: [] }; }
      buf.items.push(ol[1]);
      continue;
    }

    // Paragraph · collect consecutive plain lines into a single block.
    if (buf?.type !== 'p') { flush(); buf = { type: 'p', items: [] }; }
    buf.items.push(line);
  }
  flush();
  return blocks;
}

export default function Markdown({ source, className = '' }) {
  const blocks = toBlocks(source);
  return (
    <div className={className}>
      {blocks.map((b, i) => {
        if (b.type === 'h3') {
          return (
            <h3 key={i} className="font-display text-[1.05rem] mt-5 mb-2 first:mt-0">
              {renderInline(b.text, `h3-${i}`)}
            </h3>
          );
        }
        if (b.type === 'h4') {
          return (
            <h4 key={i} className="font-display text-[0.95rem] mt-4 mb-1.5">
              {renderInline(b.text, `h4-${i}`)}
            </h4>
          );
        }
        if (b.type === 'ul') {
          return (
            <ul key={i} className="list-disc pl-5 my-2 space-y-1">
              {b.items.map((it, j) => (
                <li key={j}>{renderInline(it, `ul-${i}-${j}`)}</li>
              ))}
            </ul>
          );
        }
        if (b.type === 'ol') {
          return (
            <ol key={i} className="list-decimal pl-5 my-2 space-y-1">
              {b.items.map((it, j) => (
                <li key={j}>{renderInline(it, `ol-${i}-${j}`)}</li>
              ))}
            </ol>
          );
        }
        // Paragraph · join the buffered lines with spaces so wrapped
        // lines inside a paragraph collapse to one block.
        return (
          <p key={i} className="my-2">
            {renderInline(b.items.join(' '), `p-${i}`)}
          </p>
        );
      })}
    </div>
  );
}
